import { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { AuthProvider } from '@/auth/auth-context';
import { ApiError } from '@/lib/errors';

/**
 * One QueryClient per browser session, created inside state so a re-render
 * never swaps it — a fresh client mid-session would drop every cache and
 * re-fire every request on screen, which on this connection is expensive.
 */
export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            /*
             * Short, because everything here moves: dispatch assigns while
             * the rider is riding, and the pharmacy packs while they are on
             * the way to it. Fifteen seconds stops a back-navigation
             * refetching the world without letting a run sheet go stale.
             */
            staleTime: 15_000,
            gcTime: 5 * 60_000,
            retry: (failureCount, error) => {
              /*
               * A refusal is an answer, not a blip. Retrying a 404, a 403 or
               * "that order is not ready to collect yet" just delays telling
               * the rider something they need to act on. Only the 5xx and
               * network cases are worth another attempt — and those are worth
               * THREE here rather than the customer app's two, because a
               * rider's connection drops for seconds at a time rather than
               * being genuinely down.
               */
              if (error instanceof ApiError && error.status < 500) return false;
              return failureCount < 3;
            },
            /*
             * Unlike the panels, this refetches on focus. A rider switches to
             * Maps for a whole leg and comes back expecting the screen to be
             * current — that switch is the single most common interaction in
             * the app, and a stale sheet after it is what makes people pull
             * to refresh out of habit.
             */
            refetchOnWindowFocus: true,
          },
          mutations: { retry: false },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>{children}</AuthProvider>
    </QueryClientProvider>
  );
}
